import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
	FiHome,        // Home
	FiCalendar,    // Fixtures
	FiActivity,    // Predictions
	FiClipboard,   // Betting Tips
	FiZap          // Premium
} from "react-icons/fi";

export default function Menu() {
	const location = useLocation();
	const [activePath, setActivePath] = useState(location.pathname);
	const [bar, setBar] = useState({ left: 0, width: 0, visible: false });
	const listRef = useRef(null);
	const itemRefs = useRef({});

	const items = [
		{ path: "/home", label: "Home", Icon: FiHome },
		{ path: "/fixtures", label: "Fixtures", Icon: FiCalendar },
		{ path: "/predictions", label: "Predictions", Icon: FiActivity },
		{ path: "/tips", label: "Betting Tips", Icon: FiClipboard },
		{ path: "/premium", label: "Premium", Icon: FiZap },
	];

	const isActive = (path) =>
		activePath === path || activePath.startsWith(path + "/");

	const updateBar = () => {
		const activeItem = items.find(i => isActive(i.path));
		if (!activeItem) {
			setBar(b => ({ ...b, visible: false }));
			return;
		}
		const el = itemRefs.current[activeItem.path];
		const list = listRef.current;
		if (!el || !list) return;

		const listRect = list.getBoundingClientRect();
		const elRect = el.getBoundingClientRect();
		const left = elRect.left - listRect.left + list.scrollLeft;
		const width = elRect.width;

		setBar({ left, width, visible: true });
	};

	useEffect(() => {
		setActivePath(location.pathname);
	}, [location.pathname]);

	useLayoutEffect(() => {
		updateBar();
		const activeItem = items.find(i => isActive(i.path));
		const el = activeItem && itemRefs.current[activeItem.path];
		el?.scrollIntoView?.({ inline: "center", behavior: "smooth", block: "nearest" });
	}, [activePath]);

	useEffect(() => {
		const onResize = () => updateBar();
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const onKeyDown = (e) => {
		if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
		e.preventDefault();
		const links = Array.from(listRef.current.querySelectorAll("a.px-navlink"));
		const idx = links.findIndex((n) => n === document.activeElement);
		if (idx === -1) {
			links[0]?.focus();
			return;
		}
		const next = e.key === "ArrowRight" ? (idx + 1) % links.length : (idx - 1 + links.length) % links.length;
		links[next]?.focus();
	};

	return (
		<>
			<ul
				className="px-menu"
				role="menubar"
				aria-label="Primary"
				ref={listRef}
				onKeyDown={onKeyDown}
			>
				<span
					className="px-menu__bar"
					style={{
						transform: `translateX(${bar.left}px)`,
						width: bar.width || 0,
						opacity: bar.visible ? 1 : 0,
					}}
					aria-hidden
				/>
				{items.map(({ path, label, Icon }) => {
					const active = isActive(path);
					return (
						<li
							key={path}
							role="none"
							style={{ padding: 0 }}
							ref={(el) => (itemRefs.current[path] = el)}
							className={`px-menu__item ${active ? "is-active" : ""}`}
						>
							<Link
								to={path}
								role="menuitem"
								className="px-navlink"
								aria-current={active ? "page" : undefined}
								tabIndex={0}
							>
								<span className="px-text">{label}</span>
								{active && <span className="sr-only">(current)</span>}
							</Link>
						</li>
					);
				})}
			</ul>

			<style>{`
        /* Container */
        .px-menu {
          --ink: #0f172a;
          --muted: #64748b;
          --brand: #2563eb;
          --brand-2: #1d4ed8;
          --hover: rgba(37,99,235,.08);
          --ring: rgba(37,99,235,.35);

          position: relative;
          display: grid;
          grid-auto-flow: column;
          gap: 6px;
          align-items: center;
          list-style: none;
          margin: 0;

          /* subtle glass card look */
          background: rgba(255,255,255,.6);
          backdrop-filter: blur(8px);

          /* mobile: horizontal scroll instead of wrapping */
          overflow-x: auto;
          overscroll-behavior-x: contain;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .px-menu::-webkit-scrollbar { display: none; }

        /* Animated active bar */
        .px-menu__bar {
          position: absolute;
          bottom: 4px;
          height: 3px;
          background: linear-gradient(90deg, var(--brand), var(--brand-2));
          border-radius: 3px;
          transition: transform .25s cubic-bezier(.2,.8,.2,1), width .25s cubic-bezier(.2,.8,.2,1), opacity .2s ease;
          pointer-events: none;
        }

        /* Items */
        .px-menu__item { position: relative; }
        .px-navlink {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 10px;
          text-decoration: none;
          color: var(--ink);
          font-weight: 700;
          letter-spacing: .01em;
          white-space: nowrap;
          transition: background .2s ease, color .2s ease, transform .18s ease, box-shadow .18s ease;
        }
        .px-navlink:hover {
          background: var(--hover);
          color: var(--brand);
        }
        .px-menu__item.is-active .px-navlink {
          color: var(--brand);
          text-shadow: 0 0 .01px currentColor; /* crisper antialiasing */
        }

        /* Icon */
        .px-icon { opacity: .9; font-size: 16px; transform: translateY(.5px); }
        .px-text { font-size: 14px; }

        /* Focus ring for keyboard users */
        .px-navlink:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px var(--ring);
          background: var(--hover);
        }

        /* Compact on small screens */
        @media (max-width: 768px) {
          .px-navlink { padding: 10px 10px; }
          .px-text { font-size: 13px; }
        }

        /* Screen reader only */
        .sr-only {
          position: absolute;
          width: 1px; height: 1px;
          padding: 0; margin: -1px;
          overflow: hidden; clip: rect(0,0,0,0);
          white-space: nowrap; border: 0;
        }
      `}</style>
		</>
	);
}
